// Reports-page language switcher helpers.

import { SURVEY_TRANSLATION_LANGUAGE_LABELS } from '@constants';
import { getSurveyLanguageOptions, getSurveyLanguageSelection } from '@helpers';

export const getSourceLanguage = (languageInfo) =>
  getSurveyLanguageSelection(languageInfo).originalLanguage;

/**
 * Ordered language options for the switcher.
 * @param {{ originalLanguage?: string, translatedLanguages?: string[], isTranslationReviewed?: boolean }} [languageInfo]
 * @returns {Array<{ label: string, value: string }>}
 */
export const getAvailableLanguages = (languageInfo) => {
  return getSurveyLanguageOptions(languageInfo, {
    includeOriginalLanguage: true,
    labelMap: SURVEY_TRANSLATION_LANGUAGE_LABELS,
  });
};

// Keep a previously selected report language only when it still exists on the newly loaded survey.
// Otherwise fall back to the survey's source language so exports and results stay aligned.
export const getValidAppliedLanguage = ({
  availableLanguages,
  currentLanguage,
  sourceLanguage,
}) => {
  if (!sourceLanguage) return currentLanguage;

  return availableLanguages.some(({ value }) => value === currentLanguage)
    ? currentLanguage
    : sourceLanguage;
};

const findTranslation = (translations, languageCode) =>
  translations?.find(
    (translation) =>
      (translation?.languageCode ?? translation?.language_code) === languageCode &&
      !translation?._delete
  );

const pickText = (translation) => translation?.text ?? translation?.title ?? undefined;
const pickRichText = (translation) => translation?.richText ?? translation?.rich_text ?? undefined;

const translateOption = (option, languageCode) => {
  const translation = findTranslation(option?.translations, languageCode);
  const text = pickText(translation);
  const richText = pickRichText(translation);
  if (text == null && richText == null) return option;
  return {
    ...option,
    ...(text != null ? { text, title: text } : {}),
    ...(richText != null ? { richText } : text != null ? { richText: undefined } : {}),
  };
};

const translateSubQuestion = (subQuestion, languageCode) => {
  const translation = findTranslation(subQuestion?.translations, languageCode);
  const text = pickText(translation);
  const richText = pickRichText(translation);
  if (text == null && richText == null) return subQuestion;
  return {
    ...subQuestion,
    ...(text != null ? { text, title: text } : {}),
    ...(richText != null ? { richText } : {}),
  };
};

const translateQuestion = (question, languageCode) => {
  const translation = findTranslation(question?.translations, languageCode);
  const text = pickText(translation);
  const richText = pickRichText(translation);

  const translated = { ...question };
  // Keep display fields aligned so the translated string wins in every renderer.
  if (text != null) {
    translated.text = text;
    translated.title = text;
    translated.richText = richText ?? undefined;
  } else if (richText != null) {
    translated.richText = richText;
  }

  if (Array.isArray(question?.options)) {
    translated.options = question.options.map((option) => translateOption(option, languageCode));
  }
  if (Array.isArray(question?.answers)) {
    translated.answers = question.answers.map((option) => translateOption(option, languageCode));
  }
  if (Array.isArray(question?.subQuestions)) {
    translated.subQuestions = question.subQuestions.map((subQuestion) =>
      translateSubQuestion(subQuestion, languageCode)
    );
  }
  return translated;
};

/**
 * Returns questions with display text swapped to the applied language.
 * @param {Object[]} questions
 * @param {string} languageCode
 * @returns {Object[]}
 */
export const applyQuestionTranslations = (questions, languageCode) => {
  if (!languageCode || !Array.isArray(questions)) return questions;
  return questions.map((question) => translateQuestion(question, languageCode));
};
